import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, FileText, Calendar, DollarSign } from 'lucide-react';
import type { Contract } from '@/lib/types/contracts';
import { ContractStatus } from '@/lib/types/enums';

interface ContractListProps {
  contracts: Contract[];
  onContractClick: (contract: Contract) => void;
  onDownloadDocument?: (documentUrl: string) => void;
}

export function ContractList({ contracts, onContractClick, onDownloadDocument }: ContractListProps) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const filteredContracts = contracts.filter((contract) => {
    const matchesSearch = contract.title.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || contract.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Active Contracts</CardTitle>
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search contracts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value={ContractStatus.ACTIVE}>Active</SelectItem>
              <SelectItem value={ContractStatus.DRAFT}>Draft</SelectItem>
              <SelectItem value={ContractStatus.PENDING_RENEWAL}>Pending Renewal</SelectItem>
              <SelectItem value={ContractStatus.COMPLETED}>Completed</SelectItem>
              <SelectItem value={ContractStatus.CANCELLED}>Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {filteredContracts.map((contract) => (
            <div
              key={contract.id}
              className="flex cursor-pointer items-center justify-between rounded-lg border p-4 transition-colors hover:bg-accent"
              onClick={() => onContractClick(contract)}
            >
              <div className="flex items-center gap-4">
                <div className="rounded-md bg-primary/10 p-2">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium">{contract.title}</h3>
                  <div className="mt-1 flex flex-wrap gap-3">
                    <Badge
                      variant={
                        contract.status === ContractStatus.ACTIVE
                          ? 'default'
                          : 'outline'
                      }
                    >
                      {typeof contract.status === 'string' 
                        ? contract.status.replace('_', ' ')
                        : contract.status}
                    </Badge>
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      Until {new Date(contract.endDate).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-1 font-medium">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  {contract.value.toLocaleString()}
                </div>
                <p className="text-sm text-muted-foreground">
                  {contract.termLength} {contract.termUnit}(s)
                </p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}